import { MenuItem, Typography } from '@material-ui/core'
import React, { useState, useReducer, useEffect } from 'react'
import {
  useMutation,
  useNotify,
  useListContext,
  sanitizeFetchType,
  GET_MANY,
  GetManyResult,
  useTranslate,
} from 'react-admin'
import { ToggleType } from '../../../../core/devices/passages/passage-reset-counters-button'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { Authority } from '../../../../core/auth/Authority'
import { PassageDto } from '../../../../core/dto/device/passages/passage.dto'
import { ResourceName } from '../../../../core/ResourceName'
import { ApiErrorDto } from '../../../../core/dto/error-handling/api-error.dto'
import { BulkActionProps } from '../../../common/BulkActionsProps'
import {
  passageResetCountersButtonInitialState,
  passageResetCountersButtonReducer,
} from '../../../../core/devices/passages/passage-reset-counters-button.reducer'
import {
  ProgressStatus,
  StateBulkProgressDialog,
} from '../../../common/StateBulkProgressDialog'
import { DateTime } from '../../../../core/common/date-time'
import { AuditCommentActionDialog } from '../../../common/AuditCommentDialog'

export const PassageResetCountersBulkButton = ({
  selectedIds,
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  innerRef,
  asRecurringJob,
  ...props
}: BulkActionProps) => {
  const { loading, refetch } = useListContext<PassageDto>()
  const notify = useNotify()
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const [{ mutateQuery, type }, dispatch] = useReducer(
    passageResetCountersButtonReducer,
    passageResetCountersButtonInitialState,
  )
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [mutate, { loading: mutationLoading }] = useMutation()
  const [bulkStateDialogOpen, setBulkStateDialogOpen] = useState(false)
  const [bulkProgressStatus, setBulkProgressStatus] = useState<
    ProgressStatus[]
  >([])
  const updateBulkProgressStatus = (newStatus: ProgressStatus) => {
    const oldStatusIndex = bulkProgressStatus.findIndex(
      (s) => s.id === newStatus.id,
    )
    bulkProgressStatus.splice(oldStatusIndex, 1, newStatus)
  }
  const [selectedPassages, setSelectedPassages] = useState<PassageDto[]>([])

  const handleMutation = async (
    passage: PassageDto,
    recurringJobName?: string,
    cronExpression?: string,
    scheduledAt?: DateTime,
    auditNote?: string,
  ) => {
    let error
    let query
    if (!asRecurringJob) {
      query = mutateQuery?.(passage, auditNote)
    } else {
      query = mutateQuery?.(
        passage,
        auditNote,
        recurringJobName,
        cronExpression,
        scheduledAt,
      )
    }
    let result = null
    try {
      result = await mutate(query, {
        returnPromise: true,
      })
    } catch (err) {
      error = err
    }
    if (error) {
      updateBulkProgressStatus({
        id: passage.id,
        status: 'error',
        error: error as ApiErrorDto,
      } as ProgressStatus)
      return error
    }
    updateBulkProgressStatus({
      id: passage.id,
      status: 'success',
    } as ProgressStatus)
    return result
  }

  const handleResetCounters = async (
    recurringJobName?: string,
    cronExpression?: string,
    scheduledAt?: DateTime,
    auditNote?: string,
  ) => {
    setBulkStateDialogOpen(true)
    setConfirmDialogOpen(false)
    const pendingRequests: Promise<any>[] = []
    const selectedPassagesResponse = (await mutate(
      {
        type: sanitizeFetchType(GET_MANY),
        resource: ResourceName.PASSAGES,
        payload: {
          ids: selectedIds,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => {
          notify(err?.message, 'error')
          setBulkStateDialogOpen(false)
        },
      },
    )) as GetManyResult<PassageDto>
    if (!selectedPassagesResponse.data.length) {
      setBulkStateDialogOpen(false)
      return
    }
    selectedPassagesResponse.data.forEach((passage: PassageDto) => {
      pendingRequests.push(
        handleMutation(
          passage,
          recurringJobName,
          cronExpression,
          scheduledAt,
          auditNote,
        ),
      )
      selectedPassages.push(passage)
      bulkProgressStatus.push({
        id: passage.id,
        status: 'pending',
      } as ProgressStatus)
    })
    await Promise.all(pendingRequests)
    refetch()
  }

  useEffect(() => {
    if (selectedIds) {
      dispatch({ type: ToggleType.RESET })
    } else {
      dispatch({ type: ToggleType.DISABLED })
    }
  }, [selectedIds])

  useEffect(() => {
    if (!bulkStateDialogOpen) {
      setBulkProgressStatus([])
      setSelectedPassages([])
    }
  }, [bulkStateDialogOpen])

  return (
    <MenuItem
      {...props}
      disabled={
        !selectedIds.length ||
        !hasAuthority(Authority.PASSAGES_COUNTERS_EDIT) ||
        loading ||
        mutationLoading ||
        type === ToggleType.DISABLED
      }
      onClick={() => setConfirmDialogOpen(true)}
      button
    >
      <Typography>
        {translate(
          !asRecurringJob
            ? 'resources.passages.actions.resetCounters.reset'
            : 'resources.passages.actions.resetCounters.reset-recurring',
        )}
      </Typography>
      <AuditCommentActionDialog
        open={confirmDialogOpen}
        handleClose={() => setConfirmDialogOpen(false)}
        mutate={handleResetCounters}
        asRecurringJob={asRecurringJob}
      />
      <StateBulkProgressDialog
        open={bulkStateDialogOpen}
        close={() => setBulkStateDialogOpen(false)}
        elements={selectedPassages}
        method="resetCounters"
        statuses={bulkProgressStatus}
        resource={ResourceName.PASSAGES}
      />
    </MenuItem>
  )
}
